import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { axiosClient } from '../../../../services/axiosClient';

const fmt = (value) =>
  value
    ? new Intl.DateTimeFormat('fr-FR', {
        dateStyle: 'short',
        timeStyle: 'short',
      }).format(new Date(value))
    : '—';

const ImpactedAppointmentsTable = ({
  unavailabilityId,
  impacts = [],
  onResolve,
  resolving,
}) => {
  const unresolved = useMemo(
    () => impacts.filter((impact) => impact.state !== 'resolved'),
    [impacts],
  );
  const [selected, setSelected] = useState([]);
  const [action, setAction] = useState('reassign');
  const [notifyClient, setNotifyClient] = useState(true);
  const [options, setOptions] = useState([]);
  const [teamMember, setTeamMember] = useState('');
  const [proposedStart, setProposedStart] = useState('');
  const [expiryHours, setExpiryHours] = useState(24);
  const [reason, setReason] = useState('Employé indisponible');
  const [error, setError] = useState('');

  useEffect(() => {
    setSelected((current) =>
      current.filter((id) => unresolved.some((row) => row.impact_id === id)),
    );
  }, [unresolved]);

  const selectedRows = useMemo(
    () => unresolved.filter((row) => selected.includes(row.impact_id)),
    [selected, unresolved],
  );

  useEffect(() => {
    let active = true;
    setTeamMember('');
    if (!selectedRows.length || !['reassign', 'propose'].includes(action)) {
      setOptions([]);
      return undefined;
    }
    Promise.all(
      selectedRows.map((row) =>
        axiosClient
          .get(
            `/schedules/unavailabilities/${unavailabilityId}/replacement-options/`,
            { params: { appointment: row.appointment } },
          )
          .then((response) => response.data || []),
      ),
    )
      .then((rows) => {
        if (!active) return;
        const common = (rows[0] || []).filter((candidate) =>
          rows.every((list) =>
            list.some(
              (item) => item.team_member_id === candidate.team_member_id,
            ),
          ),
        );
        setOptions(common);
      })
      .catch(() => active && setError('Impossible de charger les remplaçants.'));
    return () => {
      active = false;
    };
  }, [action, selectedRows, unavailabilityId]);

  const allSelected =
    unresolved.length > 0 && selected.length === unresolved.length;

  const submit = async () => {
    setError('');
    if (!selectedRows.length) {
      setError('Sélectionnez au moins un rendez-vous.');
      return;
    }
    if (['reassign', 'propose'].includes(action) && !teamMember) {
      setError('Sélectionnez un employé disponible.');
      return;
    }
    if (action === 'propose' && selectedRows.length !== 1) {
      setError('Traitez les nouvelles propositions une par une.');
      return;
    }
    if (action === 'propose' && !proposedStart) {
      setError('Sélectionnez la nouvelle date et heure.');
      return;
    }
    const operations = selectedRows.map((row) => {
      if (action === 'reassign') {
        return {
          impact: row.impact_id,
          action,
          team_member: Number(teamMember),
        };
      }
      if (action === 'propose') {
        return {
          impact: row.impact_id,
          action,
          team_member: Number(teamMember),
          proposed_start_time: new Date(proposedStart).toISOString(),
          expiry_hours: Number(expiryHours),
          message: 'Nouvelle proposition suite à une indisponibilité.',
        };
      }
      return { impact: row.impact_id, action, reason };
    });
    try {
      await onResolve({ notify_client: notifyClient, operations });
      setSelected([]);
    } catch (resolveError) {
      setError(
        resolveError?.data?.detail ||
          'Un créneau vient de changer. Actualisez puis réessayez.',
      );
    }
  };

  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>
        Rendez-vous à traiter
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {!unresolved.length ? (
        <Alert severity="success">Tous les rendez-vous ont été traités.</Alert>
      ) : (
        <>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={allSelected}
                      indeterminate={selected.length > 0 && !allSelected}
                      onChange={(event) =>
                        setSelected(
                          event.target.checked
                            ? unresolved.map((row) => row.impact_id)
                            : [],
                        )
                      }
                    />
                  </TableCell>
                  <TableCell>Cliente</TableCell>
                  <TableCell>Prestation</TableCell>
                  <TableCell>Date et heure</TableCell>
                  <TableCell>État</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {unresolved.map((row) => (
                  <TableRow key={row.impact_id} hover>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selected.includes(row.impact_id)}
                        onChange={() =>
                          setSelected((current) =>
                            current.includes(row.impact_id)
                              ? current.filter((id) => id !== row.impact_id)
                              : [...current, row.impact_id],
                          )
                        }
                      />
                    </TableCell>
                    <TableCell>{row.client_name || row.client_email}</TableCell>
                    <TableCell>{row.service_name}</TableCell>
                    <TableCell>{fmt(row.start_time)}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        color={row.state === 'proposal_pending' ? 'warning' : 'error'}
                        label={
                          row.state === 'proposal_pending'
                            ? 'Proposition en attente'
                            : 'À traiter'
                        }
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Paper variant="outlined" sx={{ mt: 2, p: 2 }}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
                gap: 2,
              }}
            >
              <FormControl fullWidth>
                <InputLabel id="absence-action-label">Action</InputLabel>
                <Select
                  labelId="absence-action-label"
                  value={action}
                  label="Action"
                  onChange={(event) => setAction(event.target.value)}
                >
                  <MenuItem value="reassign">Réaffecter</MenuItem>
                  <MenuItem value="propose">Proposer un autre créneau</MenuItem>
                  <MenuItem value="cancel">Annuler</MenuItem>
                </Select>
              </FormControl>
              {['reassign', 'propose'].includes(action) && (
                <FormControl fullWidth>
                  <InputLabel id="replacement-label">Remplaçant compatible</InputLabel>
                  <Select
                    labelId="replacement-label"
                    value={teamMember}
                    label="Remplaçant compatible"
                    onChange={(event) => setTeamMember(event.target.value)}
                  >
                    {options.map((option) => (
                      <MenuItem
                        key={option.team_member_id}
                        value={option.team_member_id}
                      >
                        {option.employee_name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              {action === 'propose' && (
                <TextField
                  label="Nouvelle date et heure"
                  type="datetime-local"
                  value={proposedStart}
                  onChange={(event) => setProposedStart(event.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              )}
              {action === 'propose' && (
                <TextField
                  label="Expiration (heures)"
                  type="number"
                  value={expiryHours}
                  onChange={(event) => setExpiryHours(event.target.value)}
                  inputProps={{ min: 1, max: 720 }}
                />
              )}
              {action === 'cancel' && (
                <TextField
                  label="Motif de l'annulation"
                  value={reason}
                  onChange={(event) => setReason(event.target.value)}
                />
              )}
            </Box>
            <Box
              sx={{
                mt: 2,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 1,
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={notifyClient}
                    onChange={(event) => setNotifyClient(event.target.checked)}
                  />
                }
                label="Prévenir la cliente"
              />
              <Button
                variant="contained"
                onClick={submit}
                disabled={resolving || !selectedRows.length}
              >
                Appliquer à la sélection
              </Button>
            </Box>
          </Paper>
        </>
      )}
    </Box>
  );
};

export default ImpactedAppointmentsTable;
